// src/state/useOmniCineNarrative.tsx
//
// OmniCine Narrative State (Authoritative)
// ---------------------------------------
// Owns *narrative meaning only*.
// Never owns timeline structure, clips, markers, or transport.
//
// Responsibilities:
// - Director mode (timeline / scene / mixed)
// - Narrative tension (0–100)
// - Storyline label (suggestive, overridable)
// - Read-only ingestion from GoalBuddy (optional)
// - Local persistence for continuity
//
// Explicit non-responsibilities:
// - No clip IDs
// - No marker state
// - No transport / viewport
// - No timeline mutation
//

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/* ---------------------------------------------------------
 * Types
 * --------------------------------------------------------- */

export type OmniCineNarrativeMode = "timeline" | "scene" | "mixed";

export interface OmniCineNarrativeState {
  mode: OmniCineNarrativeMode;

  /** Narrative tension scalar (0–100, advisory) */
  tension: number;

  /** Human-readable storyline label (optional) */
  storyline: string | null;

  /** True while ingesting optional upstream signals */
  loading: boolean;

  /** Open bag for future narrative metadata */
  meta?: Record<string, any>;
}

export interface OmniCineNarrativeContextValue
  extends OmniCineNarrativeState {
  setMode: (mode: OmniCineNarrativeMode) => void;
  setTension: (tension: number) => void;
  setStoryline: (storyline: string | null) => void;

  /** Reset narrative meaning only (never timeline) */
  resetNarrative: () => void;

  /** Immutable snapshot for Director Compiler / XR handoff */
  snapshot: () => OmniCineNarrativeState;
}

/* ---------------------------------------------------------
 * Persistence
 * --------------------------------------------------------- */

const STORAGE_KEY = "synktribe:omnicine:narrative:v1";

const DEFAULT_STATE: OmniCineNarrativeState = {
  mode: "timeline",
  tension: 0,
  storyline: null,
  loading: false,
  meta: {},
};

function loadFromStorage(): OmniCineNarrativeState | null {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as OmniCineNarrativeState;
  } catch {
    return null;
  }
}

function saveToStorage(state: OmniCineNarrativeState) {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // silent by design
  }
}

/* ---------------------------------------------------------
 * Context
 * --------------------------------------------------------- */

const OmniCineNarrativeContext =
  createContext<OmniCineNarrativeContextValue | null>(null);

/* ---------------------------------------------------------
 * Provider (NAMED, AUTHORITATIVE)
 * --------------------------------------------------------- */

export function OmniCineNarrativeProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const persisted = useMemo(() => loadFromStorage(), []);

  const [mode, setMode] = useState<OmniCineNarrativeMode>(
    persisted?.mode ?? DEFAULT_STATE.mode
  );
  const [tensionRaw, setTensionRaw] = useState<number>(
    persisted?.tension ?? DEFAULT_STATE.tension
  );
  const [storyline, setStoryline] = useState<string | null>(
    persisted?.storyline ?? DEFAULT_STATE.storyline
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [meta, setMeta] = useState<Record<string, any>>(
    persisted?.meta ?? {}
  );

  /* ---------------------------------------------------------
   * Persistence (narrative only)
   * --------------------------------------------------------- */

  useEffect(() => {
    saveToStorage({
      mode,
      tension: tensionRaw,
      storyline,
      loading,
      meta,
    });
  }, [mode, tensionRaw, storyline, loading, meta]);

  /* ---------------------------------------------------------
   * Optional GoalBuddy ingestion (READ-ONLY)
   * --------------------------------------------------------- */

  useEffect(() => {
    let mounted = true;

    async function ingestGoalBuddy() {
      setLoading(true);
      try {
        const mod = await import(
          /* webpackIgnore: true */ "@/hooks/useGoalBuddyTrajectory"
        ).catch(() => null);

        if (!mounted || !mod) return;

        if (typeof (mod as any).fetchGoalBuddySnapshot === "function") {
          const snap = await (mod as any).fetchGoalBuddySnapshot({
            domain: "cognitive",
            activa: "FlowAligned",
          });

          if (!mounted || !snap) return;

          if (typeof snap.tension === "number") {
            setTensionRaw(clamp(snap.tension));
          }

          if (typeof snap.storyline === "string") {
            setStoryline(snap.storyline);
          }
        }
      } catch {
        // silent by design
      } finally {
        if (mounted) setLoading(false);
      }
    }

    ingestGoalBuddy();
    return () => {
      mounted = false;
    };
  }, []);

  /* ---------------------------------------------------------
   * Narrative heuristic (suggestive only)
   * --------------------------------------------------------- */

  useEffect(() => {
    if (storyline) return;

    if (tensionRaw >= 75) setStoryline("Confrontation");
    else if (tensionRaw >= 45) setStoryline("Discovery");
    else if (tensionRaw >= 15) setStoryline("Calibration");
    else setStoryline("Ambient");
    // intentional: storyline excluded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tensionRaw]);

  /* ---------------------------------------------------------
   * Actions
   * --------------------------------------------------------- */

  const setTension = useCallback((t: number) => {
    setTensionRaw(clamp(t));
  }, []);

  const resetNarrative = useCallback(() => {
    setMode(DEFAULT_STATE.mode);
    setTensionRaw(DEFAULT_STATE.tension);
    setStoryline(DEFAULT_STATE.storyline);
    setMeta({});
    setLoading(false);
  }, []);

  const snapshot = useCallback<() => OmniCineNarrativeState>(() => {
    return {
      mode,
      tension: tensionRaw,
      storyline,
      loading,
      meta,
    };
  }, [mode, tensionRaw, storyline, loading, meta]);

  const value = useMemo<OmniCineNarrativeContextValue>(
    () => ({
      mode,
      tension: tensionRaw,
      storyline,
      loading,
      meta,
      setMode,
      setTension,
      setStoryline,
      resetNarrative,
      snapshot,
    }),
    [mode, tensionRaw, storyline, loading, meta, resetNarrative, snapshot]
  );

  return (
    <OmniCineNarrativeContext.Provider value={value}>
      {children}
    </OmniCineNarrativeContext.Provider>
  );
}

/* ---------------------------------------------------------
 * Hook
 * --------------------------------------------------------- */

export function useOmniCineNarrative(): OmniCineNarrativeContextValue {
  const ctx = useContext(OmniCineNarrativeContext);
  if (!ctx) {
    throw new Error(
      "useOmniCineNarrative must be used within OmniCineNarrativeProvider"
    );
  }
  return ctx;
}

/* ---------------------------------------------------------
 * Helpers
 * --------------------------------------------------------- */

function clamp(n: number, lo = 0, hi = 100) {
  if (Number.isNaN(n)) return lo;
  return Math.max(lo, Math.min(hi, Math.round(n)));
}
